/**
 * Custom LocalStorage Implementation for Pinia
 * 
 * This is a workaround due to a lack of functional keys for Pinia Persisted State storage.
 * This class implements StorageLike which is understood by Pinia and allows us to create custom keys for storage.
 * The purpose of this allows us to store the current Cirrus Reader state according to the publication (via a unique id from hashing the publication url).
 * It effectively allows us to store the state of multiple publications simultaneously.
 */

import type { StorageLike } from 'pinia-plugin-persistedstate';
import { useCirrusConfigStore} from '../stores/cirrusConfig'

class Storage implements StorageLike {
  _getKey(){
    const configStore = useCirrusConfigStore();
    const readerIdStore = useReaderIdStore();
    if (configStore.config?.public.publication?.key){
      const id = btoa(configStore.config?.public.publication.key)
      return `cirrus-reader-publication-${readerIdStore.readerId}-${id}`;
    }
    return null;
  }

  getItem(storeId: string): string | null {
    return this.getItemByStorageId(storeId)
  }

  setItem(storeId: string, serializedStoreState: string): void {
    return this.setItemByStorageId(storeId, serializedStoreState);
  }

  private getItemByStorageId(storeId: string): string | null {
    const storageKey = this._getKey();
    if (!storageKey) return null;
    const serializedState = localStorage.getItem(storageKey);
    if (typeof serializedState !== 'string') return null;
    const deserializedState = JSON.parse(serializedState);
    return Object.hasOwn(deserializedState, storeId) ? JSON.stringify(deserializedState[storeId]) : null;
  }

  private setItemByStorageId(storeId: string, serializedStoreState: string): void {
    const storageKey = this._getKey();
    if (!storageKey) return;
    const serializedState = localStorage.getItem(storageKey);
    const deserializedState = typeof serializedState === 'string' ? JSON.parse(serializedState) : {};
    deserializedState[storeId] = JSON.parse(serializedStoreState);
    localStorage.setItem(storageKey, JSON.stringify(deserializedState));
  }
}

export default new Storage()